/**
 * Consent store for analytics/tracking.
 *
 * Status model:
 *   - "unknown"  → no decision yet → banner shown, tracking BLOCKED.
 *   - "granted"  → user opted in   → tracking allowed.
 *   - "denied"   → user opted out  → tracking BLOCKED.
 *
 * Persisted in `localStorage` so the decision survives reloads. A custom
 * window event (`scuvio:consent-changed`) is emitted on every change so
 * UI components and the analytics layer can react without prop drilling.
 *
 * The store is intentionally framework-agnostic; React consumers use the
 * `useConsent` hook. The analytics module imports `getAnalyticsConsent`
 * directly to avoid circular React dependencies.
 */
import { useEffect, useState, useCallback } from "react";

export type ConsentStatus = "unknown" | "granted" | "denied";

const STORAGE_KEY = "scuvio.consent.analytics.v1";
const EVENT_NAME = "scuvio:consent-changed";
const OPEN_DIALOG_EVENT = "scuvio:consent-open";

function safeRead(): ConsentStatus {
  if (typeof window === "undefined") return "unknown";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "granted" || raw === "denied") return raw;
    return "unknown";
  } catch {
    return "unknown";
  }
}

function safeWrite(value: ConsentStatus): void {
  if (typeof window === "undefined") return;
  try {
    if (value === "unknown") {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    /* ignore quota / privacy mode */
  }
}

export function getAnalyticsConsent(): ConsentStatus {
  return safeRead();
}

export function setAnalyticsConsent(value: ConsentStatus): void {
  safeWrite(value);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<ConsentStatus>(EVENT_NAME, { detail: value }),
    );
  }
}

/** Open the consent settings dialog from anywhere in the app. */
export function openConsentSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_DIALOG_EVENT));
}

export function onConsentOpenRequest(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const wrapped = () => handler();
  window.addEventListener(OPEN_DIALOG_EVENT, wrapped);
  return () => window.removeEventListener(OPEN_DIALOG_EVENT, wrapped);
}

/**
 * React hook returning the current consent status and stable setters.
 * Re-renders subscribed components when consent changes anywhere
 * (other tabs included via the `storage` event).
 */
export function useConsent() {
  const [status, setStatus] = useState<ConsentStatus>(() => safeRead());

  useEffect(() => {
    const onChange = (event: Event) => {
      const next = (event as CustomEvent<ConsentStatus>).detail ?? safeRead();
      setStatus(next);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setStatus(safeRead());
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const accept = useCallback(() => setAnalyticsConsent("granted"), []);
  const reject = useCallback(() => setAnalyticsConsent("denied"), []);
  const reset = useCallback(() => setAnalyticsConsent("unknown"), []);

  return { status, accept, reject, reset };
}